import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, Clock, Grid, List, User, Settings } from 'lucide-react';
import { MoldOrder, TripleViewMode, UserPreference, MoldOrderMainType } from '../../types/mold';
import KanbanColumn from './KanbanColumn';
import SearchInput from './SearchInput';
import { designConfiguration, KanbanColumnStyle } from '../../constants/design';
import TableView from './TableView';
import SecondGanttChart from './SecondGanttChart';
import { usePreferences } from '../../context/PreferencesContext';
import MoldOrderTypeFilter from './MoldOrderTypeFilter';
import { sortSingleMoldOrder } from '../../utils/priority';
import { useAuth } from '../../hooks/useAuth';
import { getMoldOrdersTableColumns, getMoldOrdersColumnGroups } from './utils/tableColumns';
import ColumnSelector from '../shared/ColumnSelector';
import { getDefaultVisibleColumnsForTable } from './utils/columnConfigurations';
import Modal from '../shared/Modal';
import { useAppState } from '../../context/AppStateContext';
import { useTVBoardLogic } from '../../hooks/useTVBoardLogic';
import EmptyKanbanColumn from './EmptyKanbanColumn';
import { BoardColumn, BoardConfig } from '@/models/appState';
import { searchMoldOrder } from '@/utils/searchUtils';
import RelatedBoardButton from './RelatedBoardButton';
import { WorkflowVisualizerButton } from '../shared/WorkflowVisualizerButton';

interface BoardProps {
  moldOrders: MoldOrder[];
  preferences?: UserPreference | null;
  boardType: string;
  currentBoard: BoardConfig;
  relatedBoard?: BoardConfig;
  relatedBoardOrdersCount?: number;
  showFilterBar?: boolean;
  displayMode?: 'tv' | 'default';
}

const Board: React.FC<BoardProps> = ({
  moldOrders,
  preferences,
  boardType,
  currentBoard,
  relatedBoard,
  relatedBoardOrdersCount = 0,
  showFilterBar = true,
  displayMode = 'default',
}) => {
  const navigate = useNavigate();
  const { updatePreference } = usePreferences();
  const {
    getStatusText,
    getMoldOrderTypeLabel,
    companyCode,
    getBoardColumns,
  } = useAppState();

  const boardColumns = useMemo(() => {
    return getBoardColumns(boardType);
  }, [getBoardColumns, boardType]);

  // Use shared TV board logic - handles pagination, scroll effects, and column display
  const { kanbanScrollRef, getDisplayColumns } = useTVBoardLogic(
    displayMode,
    currentBoard.group,
    boardColumns
  );

  // Get columns to display based on TV mode and display mode
  const kanbanColumns = useMemo(() => {
    return getDisplayColumns(boardColumns);
  }, [getDisplayColumns, boardColumns]);

  // Calculate related board data
  const relatedBoardData = useMemo(() => {
    if (!relatedBoard) return null;

    return {
      title: relatedBoard.title,
      count: relatedBoardOrdersCount,
      key: relatedBoard.key,
    };
  }, [relatedBoard, moldOrders]);

  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<TripleViewMode>(preferences?.kanbanBoardView || 'kanban');
  const isNewStyleEmptyColumn = designConfiguration.kanbanColumnStyle === KanbanColumnStyle.SMALL_IMAGE;
  const showEmptyColumn = [
    KanbanColumnStyle.SMALL_IMAGE,
    KanbanColumnStyle.SMALL_COLUMN,
  ].includes(designConfiguration.kanbanColumnStyle);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrderType, setSelectedOrderType] = useState<MoldOrderMainType | 'ALL'>(preferences?.selectedOrderType ?? 'ALL');
  const [showUserTasksOnly, setShowUserTasksOnly] = useState(false);
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [isMobileActionModalOpen, setIsMobileActionModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    preferences?.moldOrderTableVisibleColumns ||
    getDefaultVisibleColumnsForTable(companyCode, 'moldOrders')
  );

  // Find the selected group for easy access to label and other properties
  const selectedGroup = useMemo(() => {
    if (selectedOrderType === 'ALL') {
      return { value: 'ALL', label: 'ทั้งหมด', subTypes: [] };
    }
    return currentBoard.filters.moldOrderTypeGroups.find(
      group => group.value === selectedOrderType
    ) || { value: selectedOrderType, label: selectedOrderType, subTypes: [] };
  }, [selectedOrderType, currentBoard.filters.moldOrderTypeGroups]);

  const filteredMoldOrders = useMemo(() => {
    return moldOrders
      .filter(order => (selectedOrderType === 'ALL' || selectedGroup.subTypes.includes(order.type)))
      .filter(order => searchMoldOrder(order, searchTerm, 'all'))
      .filter(order => (!showUserTasksOnly || (user && order.assignedTo.id === user.user_id)))
      .sort((a, b) => sortSingleMoldOrder(a, b));
  }, [moldOrders, searchTerm, selectedOrderType, selectedGroup.subTypes, showUserTasksOnly, user]);

  // Group orders by status for efficient column lookup
  const ordersByStatus = useMemo(() => {
    const groupedMap = new Map<string, MoldOrder[]>();

    filteredMoldOrders.forEach(order => {
      if (!groupedMap.has(order.status)) {
        groupedMap.set(order.status, []);
      }
      groupedMap.get(order.status)?.push(order);
    });

    return groupedMap;
  }, [filteredMoldOrders]);

  // Map orders to columns efficiently using pre-grouped ordersByStatus
  const columnOrdersMap = useMemo(() => {
    const map = new Map<string | undefined, MoldOrder[]>();

    kanbanColumns.forEach(column => {
      const orders: MoldOrder[] = [];
      column.statusIds.forEach(statusId => {
        const statusOrders = ordersByStatus.get(statusId);
        if (statusOrders) {
          orders.push(...statusOrders);
        }
      });
      map.set(column.id, orders);
    });

    return map;
  }, [kanbanColumns, ordersByStatus]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleOrderTypeChange = (newType: MoldOrderMainType | 'ALL') => {
    setSelectedOrderType(newType);
    updatePreference({ selectedOrderType: newType });
  };

  const handleViewChange = (newView: TripleViewMode) => {
    setViewMode(newView);
    updatePreference({ kanbanBoardView: newView });
  };

  const handleVisibleColumnsChange = (newVisibleColumns: Record<string, boolean>) => {
    setVisibleColumns(newVisibleColumns);
    updatePreference({ moldOrderTableVisibleColumns: newVisibleColumns });
  };

  const handleRelatedBoardClick = () => {
    if (relatedBoardData?.key) {
      navigate(`?boardType=${relatedBoardData.key}`);
    }
  };

  const paddingClass = displayMode === 'tv' ? 'pt-2 pl-2' : 'pt-1 px-2 sm:px-4';

  return (
    <div className={paddingClass}>
      {/* Compact info bar for small screens */}
      {showFilterBar && <div className="flex sm:hidden items-center justify-between gap-2 mb-3 px-2 py-2 bg-white rounded shadow text-xs">
        <div>
          ตัวกรอง: <span className="font-semibold">{selectedGroup.label}</span>
        </div>
        <div className="h-full">
          <SearchInput
            placeholder="ค้นหาใบงาน"
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full sm:w-48 text-sm h-full"
          />
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowUserTasksOnly(!showUserTasksOnly)}
            className={`p-1 rounded-full border relative ${showUserTasksOnly ? 'bg-green-100 border-green-300 text-green-700' : 'bg-white border-gray-300 text-gray-600'}`}
            title="แสดงเฉพาะงานของฉัน"
          >
            <User className="h-4 w-4" />
            {showUserTasksOnly && <CheckIcon className="h-3 w-3 text-green-700 absolute -top-1 -right-1" />}
          </button>
          <button
            onClick={() => setIsMobileActionModalOpen(true)}
            className="p-1 rounded-full border border-gray-300 bg-white text-gray-600 ml-1"
            title="ตัวเลือกเพิ่มเติม"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      </div>}

      {/* Mobile Action Modal */}
      {isMobileActionModalOpen && (
        <Modal
          hideTopPadding={true}
          onClose={() => setIsMobileActionModalOpen(false)}
          isBackgroundTransparent={true}>
          <div>
            <div className="flex flex-col gap-3">
              <div>
                <span className="block text-xs mb-1 text-gray-500">ประเภทใบงาน</span>
                <MoldOrderTypeFilter
                  onTypeCheck={handleOrderTypeChange}
                  selectedOrderType={selectedOrderType}
                  options={currentBoard.filters.moldOrderTypeGroups}
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowUserTasksOnly(!showUserTasksOnly)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-full border text-xs ${showUserTasksOnly ? 'border-2 bg-green-100 border-green-300 text-green-700' : 'bg-white border-gray-300 text-gray-600'}`}
                >
                  {showUserTasksOnly && <CheckIcon className="text-green-700 h-4 w-4" />}
                  <User className={`h-4 w-4 ${showUserTasksOnly ? 'text-green-700' : 'text-gray-500'}`} />
                  <span>งานของฉัน</span>
                </button>
              </div>
              <div>
                <span className="block text-xs mb-1 text-gray-500">มุมมอง</span>
                <div className="flex border rounded-md overflow-hidden bg-white h-9 shadow-sm">
                  <button
                    onClick={() => { handleViewChange('kanban'); setIsMobileActionModalOpen(false); }}
                    className={`flex-1 h-full px-3 py-1.5 ${viewMode === 'kanban' ? 'bg-gray-200' : 'bg-white hover:bg-gray-100'}`}
                  >
                    <Grid className="h-4 w-4 mx-auto" />
                  </button>
                  <button
                    onClick={() => { handleViewChange('table'); setIsMobileActionModalOpen(false); }}
                    className={`flex-1 h-full px-3 py-1.5 ${viewMode === 'table' ? 'bg-gray-200' : 'bg-white hover:bg-gray-100'}`}
                  >
                    <List className="h-4 w-4 mx-auto" />
                  </button>
                  <button
                    onClick={() => { handleViewChange('calendar'); setIsMobileActionModalOpen(false); }}
                    className={`flex-1 h-full px-3 py-1.5 ${viewMode === 'calendar' ? 'bg-gray-200' : 'bg-white hover:bg-gray-100'}`}
                  >
                    <Clock className="h-4 w-4 mx-auto" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Action Buttons - hidden on small screens, visible on sm+ */}
      {showFilterBar && <div className="hidden sm:flex flex-col sm:flex-row justify-between items-center gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <MoldOrderTypeFilter
            onTypeCheck={handleOrderTypeChange}
            selectedOrderType={selectedOrderType}
            options={currentBoard.filters.moldOrderTypeGroups}
          />
          <button
            onClick={() => setShowUserTasksOnly(!showUserTasksOnly)}
            className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border ${showUserTasksOnly
              ? 'border-2 bg-green-100 hover:bg-green-200 border-green-300 text-green-700'
              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-200'
              } transition-colors duration-200`}
          >
            <>
              {showUserTasksOnly && <CheckIcon className="text-green-700 h-4 w-4" />}
              <User className={`h-4 w-4 ${showUserTasksOnly ? 'text-green-700' : 'text-gray-500'}`} />
            </>
            <span className="hidden lg:block text-sm">งานของฉัน</span>
          </button>
          <SearchInput
            placeholder="ค้นหาใบงาน"
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full sm:w-48 text-sm"
          />
          {relatedBoardData && relatedBoardData.count > 0 && (
            <RelatedBoardButton
              relatedBoardTitle={relatedBoardData.title}
              relatedOrdersCount={relatedBoardData.count}
              onClick={handleRelatedBoardClick}
            />
          )}
        </div>
        <div className="flex items-center justify-end gap-2 h-9">
          <WorkflowVisualizerButton orderType={selectedOrderType} />
          {viewMode === 'table' && (
            <button
              onClick={() => setIsColumnSelectorOpen(true)}
              className="p-2 rounded hover:bg-gray-100 h-full"
            >
              <Settings size={18} />
            </button>
          )}
          <div className="flex border rounded-md overflow-hidden bg-white h-full shadow-sm">
            <button
              onClick={() => handleViewChange('kanban')}
              className={`h-full px-3 py-1.5 ${viewMode === 'kanban' ? 'bg-gray-200' : 'bg-white hover:bg-gray-100'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleViewChange('table')}
              className={`h-full px-3 py-1.5 ${viewMode === 'table' ? 'bg-gray-200' : 'bg-white hover:bg-gray-100'}`}
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleViewChange('calendar')}
              className={`h-full px-3 py-1.5 ${viewMode === 'calendar' ? 'bg-gray-200' : 'bg-white hover:bg-gray-100'}`}
            >
              <Clock className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>}


      {/* Board Content */}
      {viewMode === 'kanban' ? (
        <div
          ref={kanbanScrollRef}
          className="flex flex-1 gap-4 overflow-x-scroll pb-6 h-fit"
        >
          {kanbanColumns.map((column: BoardColumn) => {
            const columnOrders = columnOrdersMap.get(column.id) || [];
            const isEmpty = columnOrders.length === 0;

            if (isEmpty && showEmptyColumn) {
              return (<EmptyKanbanColumn
                key={'empty-column-' + column.id}
                status={column}
                isNewStyleEmptyColumn={isNewStyleEmptyColumn}
              />);
            }

            return (
              <div
                key={column.id}
                className="flex-shrink-0 w-[280px] sm:w-[320px]"
              >
                <KanbanColumn
                  moldOrders={columnOrders}
                  column={column}
                  boardType={currentBoard.group}
                  displayMode={displayMode}
                />
              </div>
            );
          })}
        </div>
      ) : viewMode === 'table' ? (
        <div className="flex-1 overflow-scroll mb-10 relative z-10">
          <TableView
            moldOrders={filteredMoldOrders}
            visibleColumns={visibleColumns}
            getStatusText={getStatusText}
            getMoldOrderTypeLabel={getMoldOrderTypeLabel}
            boardType={currentBoard.group}
            companyCode={companyCode}
          />
        </div>
      ) : (
        <div className="flex-1 h-full overflow-auto">
          <SecondGanttChart moldOrders={filteredMoldOrders} />
        </div>
      )}

      {/* Column Selector Modal */}
      <ColumnSelector
        isOpen={isColumnSelectorOpen}
        onClose={() => setIsColumnSelectorOpen(false)}
        columns={getMoldOrdersTableColumns({
          getStatusText,
          companyCode,
          getMoldOrderTypeLabel,
        })}
        columnGroups={getMoldOrdersColumnGroups({ getStatusText, companyCode })}
        visibleColumns={visibleColumns}
        setVisibleColumns={handleVisibleColumnsChange}
      />
    </div>
  );
};

export default Board;